import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Building, Search, Plus, Check, X, Loader } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * GallerySelector - A combo search component that:
 * 1. Searches existing galleries in our database
 * 2. Falls back to Google Places (via Edge Function) for new venues
 * 3. Can auto-create a gallery when a Places result is selected
 */
const GallerySelector = ({ 
  value,           // { gallery_id, name, address } or null
  onChange,        // (selection) => void
  placeholder = "Search galleries or enter new venue..."
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [galleries, setGalleries] = useState([]);
  const [placesResults, setPlacesResults] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search galleries in our database
  const searchGalleries = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setGalleries([]);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('galleries')
        .select('gallery_id, name, address')
        .or(`name.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`)
        .order('name')
        .limit(10);

      if (error) throw error;
      setGalleries(data || []);
    } catch (err) {
      console.error('Error searching galleries:', err);
      setGalleries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search Google Places via Edge Function
  const searchPlaces = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setPlacesResults([]);
      return;
    }

    try {
      setPlacesLoading(true);
      
      const { data: authData } = await supabase.auth.getSession();
      const token = authData?.session?.access_token;
      
      const response = await fetch('https://api.vibrantcast.com/functions/v1/google_maps_location', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          input: searchQuery,
          user_lat: 41.8781, // Chicago default
          user_lng: -87.6298,
          radius: 50
        })
      });

      if (!response.ok) {
        throw new Error('Failed to search locations');
      }

      const results = await response.json();
      
      if (results && results.length > 0) {
        setPlacesResults(results.slice(0, 5));
      } else {
        setPlacesResults([]);
      }
    } catch (err) {
      console.error('Error searching places:', err);
      setPlacesResults([]);
    } finally {
      setPlacesLoading(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      searchGalleries(searchQuery);
      searchPlaces(searchQuery);
    }, 300),
    [searchGalleries, searchPlaces]
  );

  // Trigger search when query changes
  useEffect(() => {
    if (query.trim().length >= 2) {
      debouncedSearch(query);
    } else {
      setGalleries([]);
      setPlacesResults([]);
    }
  }, [query, debouncedSearch]);

  // Handle selecting an existing gallery
  const handleSelectGallery = (gallery) => {
    onChange({
      gallery_id: gallery.gallery_id,
      name: gallery.name,
      address: gallery.address,
      isNew: false
    });
    setQuery('');
    setIsOpen(false);
  };

  // Handle selecting a Google Places result
  const handleSelectPlace = async (place) => {
    setLoading(true);
    
    try {
      // Check if this place already exists in our database by name (case-insensitive)
      const { data: existingGalleries } = await supabase
        .from('galleries')
        .select('gallery_id, name, address')
        .ilike('name', place.name || place.formatted_address.split(',')[0]);

      if (existingGalleries && existingGalleries.length > 0) {
        // Use existing gallery
        const existingGallery = existingGalleries[0];
        onChange({
          gallery_id: existingGallery.gallery_id,
          name: existingGallery.name,
          address: existingGallery.address,
          isNew: false
        });
      } else {
        // Create new gallery
        const { data: { user } } = await supabase.auth.getUser();
        
        const newGalleryData = {
          name: place.name || place.formatted_address.split(',')[0],
          address: place.formatted_address,
          point_location: place.latitude && place.longitude 
            ? `${place.latitude},${place.longitude}` 
            : null,
          geo_point: place.geo_point || null,
          approved: true,
          type: 'gallery',
          added_by: user?.id || null
        };

        const { data: newGallery, error } = await supabase
          .from('galleries')
          .insert([newGalleryData])
          .select('gallery_id, name, address')
          .single();

        if (error) {
          console.error('Error creating gallery:', error);
          // Still provide the info without gallery_id
          onChange({
            gallery_id: null,
            name: newGalleryData.name,
            address: place.formatted_address,
            isNew: true,
            createFailed: true
          });
        } else {
          onChange({
            gallery_id: newGallery.gallery_id,
            name: newGallery.name,
            address: newGallery.address,
            isNew: true
          });
        }
      }
    } catch (err) {
      console.error('Error handling place selection:', err);
    } finally {
      setLoading(false);
      setQuery('');
      setIsOpen(false);
    }
  };

  // Clear selection
  const handleClear = () => {
    onChange(null);
    setQuery('');
  };

  const showDropdown = isOpen && (galleries.length > 0 || placesResults.length > 0 || loading || placesLoading);
  const hasResults = galleries.length > 0 || placesResults.length > 0;

  return (
    <div className="gallery-selector">
      {value ? (
        // Selected state
        <div className="gallery-selected">
          <div className="gallery-selected-info">
            <Building size={16} className="gallery-icon" />
            <div className="gallery-details">
              <span className="gallery-name">{value.name}</span>
              {value.address && (
                <span className="gallery-address">{value.address}</span>
              )}
              {value.isNew && !value.createFailed && (
                <span className="gallery-new-badge">
                  <Check size={12} /> New gallery added
                </span>
              )}
            </div>
          </div>
          <button type="button" className="clear-btn" onClick={handleClear}>
            <X size={16} />
          </button>
        </div>
      ) : (
        // Search state
        <div className="gallery-search-container">
          <div className="gallery-search-input">
            <Search size={16} className="search-icon" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              autoComplete="off"
            />
            {(loading || placesLoading) && (
              <Loader size={16} className="loading-icon spinner" />
            )}
          </div>

          {showDropdown && (
            <div ref={dropdownRef} className="gallery-dropdown">
              {/* Existing Galleries Section */}
              {galleries.length > 0 && (
                <div className="dropdown-section">
                  <div className="dropdown-section-header">
                    <Building size={14} />
                    Existing Galleries
                  </div>
                  {galleries.map((gallery) => (
                    <button
                      key={gallery.gallery_id}
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleSelectGallery(gallery)}
                    >
                      <div className="item-content">
                        <span className="item-name">{gallery.name}</span>
                        {gallery.address && (
                          <span className="item-address">{gallery.address}</span>
                        )}
                      </div>
                      <Check size={14} className="item-check" />
                    </button>
                  ))}
                </div>
              )}

              {/* Google Places Section */}
              {placesResults.length > 0 && (
                <div className="dropdown-section">
                  <div className="dropdown-section-header">
                    <MapPin size={14} />
                    Add New Venue
                  </div>
                  {placesResults.map((place, index) => (
                    <button
                      key={index}
                      type="button"
                      className="dropdown-item new-venue"
                      onClick={() => handleSelectPlace(place)}
                    >
                      <div className="item-content">
                        <span className="item-name">
                          {place.name || place.formatted_address.split(',')[0]}
                        </span>
                        <span className="item-address">
                          {place.formatted_address}
                        </span>
                      </div>
                      <Plus size={14} className="item-add" />
                    </button>
                  ))}
                </div>
              )}

              {/* Loading state */}
              {(loading || placesLoading) && !hasResults && (
                <div className="dropdown-loading">
                  <Loader size={16} className="spinner" />
                  <span>Searching...</span>
                </div>
              )}

              {/* No results */}
              {!loading && !placesLoading && query.trim().length >= 2 && !hasResults && (
                <div className="dropdown-empty">
                  <span>No galleries or venues found</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GallerySelector;
